import {
	getInvestorInvestmentsAndTransactions,
	getMIPInvestorDetailsData,
	getMIPInvestorInvestmentValuationsData,
} from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

export const useManageInvestorDetailsController = () => {
	const [showReviewModal, setShowReviewModal] = useState(false);
	const { investorId } = useParams();

	const contractTableRef = useRef<HTMLDivElement>(null);

	const getMIPInvestorDetailsDataRequest = useQuery({
		queryKey: [API_PATHS.MIP_INVESTOR_GET_INVESTOR_BY_ID_ID_GET, { investorId }],
		queryFn: ({ signal }) =>
			getMIPInvestorDetailsData(
				API_PATHS.MIP_INVESTOR_GET_INVESTOR_BY_ID_ID_GET,
				{ investorId },
				signal
			),
		enabled: !!investorId,
		refetchInterval: 5 * 60 * 1000,
		retry: false,
	});

	const getMIPInvestorInvestmentValuationsRequest = useQuery({
		queryKey: [API_PATHS.MIP_INVESTOR_VALUATIONS_ID_GET, { investorId }],
		queryFn: ({ signal }) =>
			getMIPInvestorInvestmentValuationsData(
				API_PATHS.MIP_INVESTOR_VALUATIONS_ID_GET,
				{ investorId },
				signal
			),
		enabled: !!investorId,
	});

	const getInvestmentsAndTransactionsRequest = useQuery({
		queryKey: [
			API_PATHS.MIP_INVESTOR_GET_INVESTMENTS_TRANSACTIONS_INVESTORID_GET,
			{ investorId: investorId as string },
		],
		queryFn: ({ signal }) =>
			getInvestorInvestmentsAndTransactions({ investorId: investorId as string }, signal),
		enabled: !!investorId,
	});

	// Contract list endpoint (MIP_INVESTOR_ID_CONTRACT_LIST_GET) was removed in DC-807.
	// Provide a stub so consumers that destructure getMIPInvestorContractListRequest still compile.
	const getMIPInvestorContractListRequest = {
		data: undefined as undefined,
		refetch: () => Promise.resolve(),
	};

	const refreshMIPInvestorDetailsData = () => {
		if (!investorId) {
			return;
		}
		void getMIPInvestorDetailsDataRequest.refetch();
		void getInvestmentsAndTransactionsRequest.refetch();
	};

	return {
		getMIPInvestorContractListRequest,
		investorContracts: undefined as undefined,
		investorDetailsData: getMIPInvestorDetailsDataRequest.data?.payload,
		isFetchingInvestments: getInvestmentsAndTransactionsRequest.isFetching,
		investments: getInvestmentsAndTransactionsRequest.data?.payload,
		investmentsLoading:
			getInvestmentsAndTransactionsRequest.isLoading ||
			getInvestmentsAndTransactionsRequest.isFetching,
		getMIPInvestorDetailsDataRequest,
		investorValuationsData: getMIPInvestorInvestmentValuationsRequest.data?.payload,
		valuationsLoading:
			getMIPInvestorInvestmentValuationsRequest.isLoading ||
			getMIPInvestorInvestmentValuationsRequest.isFetching,
		refetchInvestorValuations: getMIPInvestorInvestmentValuationsRequest.refetch,
		investorId,
		contractTableRef,
		refreshMIPInvestorDetailsData,
		showReviewModal,
		setShowReviewModal,
	};
};
